import React, { JSX, useState } from 'react';
import { Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import dayjs from 'dayjs';
import { useFormContext } from '../Form';
import DatePicker from '../../Components/Input/DatePicker/DatePicker';
import useConfig from '../../Hooks/useConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import useAppService from '../../Hooks/useAppService';
import { sanitize } from '@ibe/services';
import { DatePickerProps } from '../../Components/Input/DatePicker/DatePickerInModal';
import classNames from 'classnames';
import { getErrorMessage } from './utils';

interface Props extends Omit<DatePickerProps, 'onChange' | 'icon'> {
  name: string;
  baseName?: string;
  id?: string;
  label?: string;
  boldLabel?: boolean;
  errorsProp?: Record<string, string>;
  dataTestId: string;
  selectedDate?: (date: string) => Date;
  showErrors?: boolean;
  showErrorIcon?: boolean;
  icon?: IconProp;
  placeholderText?: string;
  withKeyboardSupport?: boolean;
  showInModal?: boolean;
  displayFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  inputClassName?: string;
  useFormValidation?: boolean;
  inLine?: boolean;
  modalLabel?: string;
  noInitialValueInput?: boolean;
  validateOnBlurOnly?: boolean;
  showSuccessState?: boolean;
}

const FormDatePicker = (props: Props): JSX.Element | null => {
  const {
    errorsProp,
    label,
    name,
    id,
    dataTestId,
    baseName,
    selectedDate,
    showErrors,
    boldLabel,
    showErrorIcon,
    placeholderText,
    withKeyboardSupport,
    showInModal,
    modalLabel,
    inLine,
    noInitialValueInput,
    validateOnBlurOnly,
    showSuccessState
  } = props;
  const { icon, ...rest } = props;
  const { register, errors, control, formState, trigger } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};
  const config = useConfig();
  const appService = useAppService();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState<boolean>(false);
  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldName = baseName ?? name;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  const hasError = !!formErrors?.[fieldName];
  const isTouched = !!touchedFields?.[fieldName];
  const fieldValue = _formValues?.[fieldName];
  const hasValue = typeof fieldValue === 'string' ? fieldValue.length > 0 : !!fieldValue;

  const displayError = validateOnBlurOnly
    ? showErrors && hasError && (isSubmitted || isTouched || hasBeenBlurred) && !isFocused
    : showErrors && hasError;

  const displaySuccess = showSuccessState ? !hasError && hasValue && !isFocused : false;

  const getDate = (date: string): Date | undefined => {
    if (date) {
      const dateParsed = date.split('-');
      return new Date(Number(dateParsed[0]), Number(dateParsed[1]) - 1, Number(dateParsed[2]));
    } else {
      return undefined;
    }
  };

  const handleChange = (date: Date, onFieldChange: (...event: unknown[]) => void): void => {
    onFieldChange(date ? dayjs(date).format(config.formatDate) : undefined);
  };

  return (
    <div
      className={classNames('form__datepicker form-input', {
        'input--error': displayError,
        'form-input--errors': displayError,
        'form-input--touched': isTouched,
        'form-input--dirty': dirtyFields?.[fieldName],
        'form-input--form-submitted': isSubmitted,
        'form-input--success': displaySuccess
      })}
    >
      {!!label && (
        <Label
          className={`${displayError ? 'form__error__top ' : ''}${
            boldLabel ? 'font-weight-bold' : ''
          }`}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }): JSX.Element => {
          return (
            <div className="position-relative">
              <DatePicker
                className={!!field.value ? 'input--filled' : ''}
                /* eslint-disable */
                {...rest}
                {...field}
                /* eslint-enable */
                id={id}
                onFocus={() => setIsFocused(true)}
                onBlur={async () => {
                  field.onBlur();
                  setIsFocused(false);
                  setHasBeenBlurred(true);
                  if (trigger) await trigger(fieldName);
                }}
                onChange={(date: Date): void => handleChange(date, field.onChange)}
                onInvalidInputFormat={() => field.onChange(null)}
                date={selectedDate ? selectedDate(field.value) : getDate(field.value)}
                dataTestId={dataTestId}
                placeholderText={placeholderText}
                withKeyboardSupport={withKeyboardSupport}
                showInModal={showInModal}
                displayFormat={config.displayFormatDate[appService.lang.replace('-', '_')]}
                inline={inLine}
                modalLabel={modalLabel}
                noInitialValueInput={noInitialValueInput}
              />
              {icon && (
                <div className="form__datepicker__icon__container">
                  <FontAwesomeIcon className="form__datepicker__icon" icon={icon} />
                </div>
              )}
              {displaySuccess && (
                <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />
              )}
            </div>
          );
        }}
      />
      {displayError && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
      {showErrorIcon && displayError && (
        <FontAwesomeIcon icon={faExclamationTriangle} className="form-input__error-icon" />
      )}
    </div>
  );
};

export default FormDatePicker;
